import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';

import {
    GoBackButton,
    Text,
    ContainedButton,
    OverlapCard,
    StyledDialog,
    DeleteIcon2,
    OutlinedButton,
    ConfirmDialog,
} from '../../components/index';

import CreateSessionCard from '../../components/Provider/Appointment/CreateSessionCard';
import ProviderCard from '../../components/Provider/Appointment/ProviderCard';
import PatientCard from '../../components/Provider/Appointment/PatientCard';
import RulesCard from '../../components/Provider/Appointment/RulesCard';
import SessionTypeCard from '../../components/Provider/Appointment/SessionTypeCard';

import { providerPageWidth, OPEN_RULES_TH } from '../../constant';

import { createOpen, editOpen, deleteOpen, GET_CALENDAR, GET_MEET } from '../../services/provider';
import { alertError, alertSuccess } from '../../redux/stateActions';

dayjs.extend(objectSupport);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const CreateOpen = ({ history, location }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const { id } = useParams();
    const matchEdit = useRouteMatch('/provider/calendar/open/edit/:id');

    useEffect(() => {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const date = queryParams.get('date');
        const staff = queryParams.get('staff');

        if (date && dayjs.isDayjs(dayjs(date))) {
            setAppointmentDate(dayjs(date));
        }
        if (staff) {
            setSelectedStaff(staff);
        } else if (
            !_.isEmpty(user) &&
            !user.staff.deactivated &&
            user.staff.staff_role.name !== 'Admin'
        ) {
            setSelectedStaff(user.staff.id);
        }
    }, []);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedRules, setSelectedRules] = useState([]);
    const [calendar, setCalerdar] = useState({});
    const [overlapData, setOverlapData] = useState({});
    const [numberOfParticipants, setNumberOfParticipants] = useState(1);
    const [selectedStaff, setSelectedStaff] = useState('');
    const [sessionType, setSessionType] = useState('public');
    const [disabledEditMeet, setDisabledEditMeet] = useState(false);

    const handleGoBack = () => {
        history.goBack();
    };

    /*--------------- Get Calendar ---------------*/
    const { data: getCalendarData, error: getCalendarError } = useQuery(GET_CALENDAR, {
        variables: {
            meet: matchEdit ? id : undefined,
            staff: selectedStaff,
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format('YYYY-MM-DD'),
        },
        skip: _.isEmpty(user) || _.isNull(appointmentDate) || !selectedStaff,
    });

    useEffect(() => {
        if (getCalendarError) {
            dispatch(alertError(`Cannot get group and 1on1 session: ${getCalendarError.message}`));
        }

        if (getCalendarData && getCalendarData.calendars.length > 1) {
            dispatch(alertError(`More than one calendar found`));
            return;
        }

        if (getCalendarData) {
            if (_.isEmpty(getCalendarData.calendars)) {
                setCalerdar({});
            } else {
                setCalerdar(getCalendarData.calendars[0]);
            }
        }
    }, [getCalendarData, getCalendarError]);

    /*--------------- Get Meet for edit ---------------*/
    const {
        data: getMeetData,
        error: getMeetError,
        loading: getMeetLoading,
    } = useQuery(GET_MEET, {
        variables: { id: id },
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
            handleGoBack();
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            const {
                type,
                name,
                description,
                startTime,
                endTime,
                staff,
                sessions,
                rules,
                startDate,
                numberOfParticipants,
                sessionType,
            } = getMeetData.meet;

            console.log('getMeetData.meet', getMeetData.meet);

            if (type !== 'open') {
                handleGoBack();
                return;
            }

            const endDateTime = dayjs(startDate).set({
                hour: endTime.split(':')[0],
                minute: endTime.split(':')[1],
            });

            if (matchEdit && dayjs().isAfter(endDateTime, 'minute')) {
                handleGoBack();
                return;
            }

            if (sessions.length === 0) return;
            if (sessions.length > 1) {
                dispatch(alertError(`More than one session found`));
                return;
            }

            setName(name);
            setDescription(description);
            setSelectedStaff(staff.id);
            setNumberOfParticipants(numberOfParticipants);
            setSessionType(sessionType);

            if (matchEdit) {
                setAppointmentDate(dayjs(startDate));
            }

            // set rules
            const fetchRules = Array.from(rules);
            const newSelectedRules = OPEN_RULES_TH.map((r) =>
                fetchRules.includes(r.id) ? { ...r, checked: true } : { ...r, checked: false }
            );
            console.log('newSelectedRules', newSelectedRules);
            setSelectedRules(newSelectedRules);

            // set start time
            setAppointmentStartTime(
                dayjs()
                    .set({
                        hour: startTime.split(':')[0],
                        minute: startTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            // set end time
            setAppointmentEndTime(
                dayjs()
                    .set({
                        hour: endTime.split(':')[0],
                        minute: endTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            // set patient
            const newSelectedPatients = [];
            sessions[0].patient_summaries
                .filter((item) => item?.patient?.user)
                .forEach((summary) => {
                    newSelectedPatients.push({
                        id: summary.patient.id,
                        HNCode: summary.patient.HNCode,
                        user: summary.patient.user,
                    });
                });
            setSelectedPatients(newSelectedPatients);

            if (sessions[0].roomId) {
                setDisabledEditMeet(true);
            }
        }
    }, [getMeetError, getMeetData]);

    useEffect(() => {
        const newSelectedRules = OPEN_RULES_TH.map((rule) => ({ ...rule, checked: false }));
        setSelectedRules(newSelectedRules);
    }, []);

    const handleSelectStaff = (id) => {
        setSelectedStaff(id);
        setErrors(errors.filter((error) => error.property !== 'selectedProvider'));
    };

    const handleAddPatient = (patientArr) => {
        setSelectedPatients(patientArr);
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleRemovePatient = (id) => {
        setSelectedPatients(selectedPatients.filter((patient) => patient.id !== id));
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentStartTime'));
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentEndTime'));
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
    };

    const handlePatientTimeChange = () => {
        return;
    };

    const handleNumberOfParticipantsChange = (e) => {
        setNumberOfParticipants(e.target.value);
    };

    const handleSessionType = (value) => {
        setSessionType(value);
    };

    const handleSelectRule = (id) => {
        const newSelectedRules = [...selectedRules];

        const selectedRuleIndex = newSelectedRules.findIndex((r) => r.id === id);

        if (newSelectedRules[selectedRuleIndex].checked === true) {
            newSelectedRules[selectedRuleIndex].checked = false;
        } else {
            newSelectedRules[selectedRuleIndex].checked = true;
        }

        setSelectedRules(newSelectedRules);
    };

    const onCancelConfirm = () => {
        setOverlapData({});
    };

    const handleSubmit = async () => {
        setOverlapData({});

        const data = {
            id: id,
            hospital: user.staff.hospital.id,
            sessionType,
            name: name.trim(),
            description: description.trim(),
            staff: selectedStaff,
            numberOfParticipants,
            patients: selectedPatients.map((patient) => ({
                id: patient.id,
            })),
            date: dayjs(appointmentDate).format('YYYY-MM-DD'),
            startTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentStartTime.split(':')[0])
                .set('minute', appointmentStartTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            endTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentEndTime.split(':')[0])
                .set('minute', appointmentEndTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            rules: selectedRules.filter((r) => r.checked).map((r) => r.id),
        };

        try {
            setLoading(true);

            console.log('data', data);

            let res;

            if (matchEdit) {
                res = await editOpen(data);
            } else {
                res = await createOpen(data);
            }

            console.log('res', res);

            if (res.status === 200) {
                dispatch(alertSuccess(`Open Session ${matchEdit ? 'Edited' : 'Created'}`));

                if (matchEdit) {
                    history.push(`/provider/session/open/${id}`);
                } else {
                    history.push(`/provider/calendar`);
                }
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot create Open Session: ${JSON.stringify(error.response?.data?.message)}`
                )
            );
        }
    };

    const handleSave = async () => {
        const newErrors = [];

        if (name === '') {
            newErrors.push({
                property: 'name',
                message: 'Please provide a valid Name.',
            });
        }

        if (description === '') {
            newErrors.push({
                property: 'description',
                message: 'Please provide a valid Description.',
            });
        }

        if (appointmentDate === null) {
            newErrors.push({
                property: 'appointmentDate',
                message: 'Please provide a valid Date.',
            });
        }

        if (appointmentStartTime === '') {
            newErrors.push({
                property: 'appointmentStartTime',
                message: 'Please provide a valid Start Time.',
            });
        }

        if (appointmentEndTime === '') {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Please provide a valid End Time.',
            });
        }

        if (selectedStaff === '') {
            newErrors.push({
                property: `selectedProvider`,
                message: 'Please select provider.',
            });
        }

        if (
            dayjs()
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrAfter(
                    dayjs().set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    }),
                    'minute'
                )
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'End Time must be after Start Time.',
            });
        }

        if (
            appointmentDate !== null &&
            (dayjs(appointmentDate)
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrBefore(dayjs()) ||
                dayjs(appointmentDate)
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isSameOrBefore(dayjs()))
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Session must be in the future.',
            });
        }

        if (selectedPatients.length > numberOfParticipants) {
            newErrors.push({
                property: 'numberOfParticipants',
                message: 'Number of participant exceeded.',
            });
        }

        setErrors(newErrors);
        if (!_.isEmpty(newErrors)) return;

        // check meet time overlap
        if (!_.isEmpty(calendar)) {
            const { meets } = calendar;
            const startTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            const endTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            if (startTimeOverlap) {
                setOverlapData(startTimeOverlap);
                return;
            }
            if (endTimeOverlap) {
                setOverlapData(endTimeOverlap);
                return;
            }
        }

        handleSubmit();
    };

    const [openDelete, setOpenDelete] = useState(false);

    const onDelete = () => {
        setOpenDelete(true);
    };

    const onCancelDelete = () => {
        setOpenDelete(false);
    };

    const onConfirmDelete = async () => {
        try {
            setLoading(true);

            let res = await deleteOpen(id);

            console.log('res', res);

            if (res?.status === 200) {
                dispatch(alertSuccess(`Open session Deleted`));

                history.push(`/provider/calendar`);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            console.log('error', error.response);
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot delete open Session: ${JSON.stringify(error.response?.data?.message)}`
                )
            );
        }
    };

    return (
        <div
            style={{
                padding: '41px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '20px',
            }}
        >
            <ConfirmDialog
                open={openDelete}
                title="Are you sure you want to delete this Session?"
                contentText="Please confirm if you want to proceed with the deletion."
                contentTextColor="gray2"
                cancelText="Cancel"
                onCancel={onCancelDelete}
                confirmText="Yes, Delete"
                onConfirm={onConfirmDelete}
                loading={loading}
            />
            <StyledDialog
                scroll="body"
                open={!_.isEmpty(overlapData)}
                onClose={onCancelConfirm}
                aria-labelledby="warningDialog"
                aria-describedby="warning dialog"
                PaperProps={{ style: { borderRadius: '16px' } }}
            >
                <OverlapCard
                    data={overlapData}
                    onCancelConfirm={onCancelConfirm}
                    onConfirm={handleSubmit}
                />
            </StyledDialog>
            <GoBackButton $text onClick={handleGoBack} />
            <Text $fontSize="20px" $bold>
                {`${matchEdit ? 'Edit' : 'Create'} Open session`}
            </Text>
            <CreateSessionCard
                errors={errors}
                loading={loading || getMeetLoading}
                disabledDate={disabledEditMeet}
                disabledTime={disabledEditMeet}
                appointmentDate={appointmentDate}
                handleDateChange={handleDateChange}
                appointmentStartTime={appointmentStartTime}
                handleStartTimeChange={handleStartTimeChange}
                appointmentEndTime={appointmentEndTime}
                handleEndTimeChabge={handleEndTimeChabge}
                name={name}
                handleNameChange={handleNameChange}
                description={description}
                handleDescriptionChange={handleDescriptionChange}
                selectedStaff={selectedStaff}
                setSelectedStaff={setSelectedStaff}
            />
            <SessionTypeCard
                selectedValue={sessionType}
                onChange={handleSessionType}
                disabled={disabledEditMeet}
            />
            {user.staff.staff_role.name !== 'Provider' && (
                <ProviderCard
                    errors={errors}
                    loading={loading}
                    selectedStaff={selectedStaff}
                    handleSelectStaff={handleSelectStaff}
                    disabled={disabledEditMeet}
                />
            )}
            <PatientCard
                layout="OPEN"
                errors={errors}
                loading={loading}
                selectedPatients={selectedPatients}
                handleAddPatient={handleAddPatient}
                handlePatientTimeChange={handlePatientTimeChange}
                handleRemovePatient={handleRemovePatient}
                numberOfParticipants={numberOfParticipants}
                handleNumberOfParticipantsChange={handleNumberOfParticipantsChange}
            />
            <RulesCard rules={selectedRules} onSelect={handleSelectRule} />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: matchEdit ? 'space-between' : 'flex-end',
                }}
            >
                {matchEdit && (
                    <OutlinedButton
                        $color="pink"
                        $width="99px"
                        $borderWidth="1.5px"
                        $padding="0"
                        onClick={onDelete}
                        disabled={disabledEditMeet}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: '3px',
                            }}
                        >
                            <DeleteIcon2
                                width="20px"
                                height="20px"
                                fill={disabledEditMeet ? 'gray4' : 'pink'}
                            />
                            <Text $color={disabledEditMeet ? 'gray4' : 'pink'}>Delete</Text>
                        </div>
                    </OutlinedButton>
                )}
                <ContainedButton
                    style={{ height: '32px', width: '93px' }}
                    onClick={handleSave}
                    disabled={
                        name === '' ||
                        description === '' ||
                        (sessionType === 'invite' && _.isEmpty(selectedPatients)) ||
                        (user.staff.staff_role.name === 'Provider' && user.staff.deactivated)
                    }
                    loading={loading}
                >
                    Next
                </ContainedButton>
            </div>
        </div>
    );
};

CreateOpen.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default CreateOpen;
